export class AdminQueryLogAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = "AdminQueryLogAuthError";
  }
}

const getApiUrl = () =>
  process.env.REACT_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:8000`;

const getAccessToken = () => {
  const token = localStorage.getItem("access_token");
  if (!token) {
    throw new Error("Authentication required. Please log in again.");
  }
  return token;
};

const getValidatedAccessToken = () => {
  const token = getAccessToken();
  const tokenParts = token.split(".");
  if (tokenParts.length !== 3) {
    localStorage.removeItem("access_token");
    throw new AdminQueryLogAuthError(
      "Invalid authentication token. Please log in again.",
    );
  }
  return token;
};

const authJsonHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

const throwForFailedResponse = (response) => {
  if (response.status === 401) {
    throw new AdminQueryLogAuthError("Authentication failed. Please log in again.");
  }
  throw new Error(`HTTP error! status: ${response.status}`);
};

export const fetchSearchLogs = async ({
  page,
  perPage,
  start,
  end,
}) => {
  const token = getValidatedAccessToken();
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
  });
  params.append("start", start);
  params.append("end", end);

  const response = await fetch(`${getApiUrl()}/api/v1/admin/search-logs?${params}`, {
    headers: authJsonHeaders(token),
  });
  if (!response.ok) {
    throwForFailedResponse(response);
  }
  return response.json();
};

export const exportSearchLogs = async ({ start, end }) => {
  const token = getAccessToken();
  const response = await fetch(`${getApiUrl()}/api/v1/admin/search-logs/export`, {
    method: "POST",
    headers: authJsonHeaders(token),
    body: JSON.stringify({ start, end }),
  });
  if (!response.ok) {
    throwForFailedResponse(response);
  }
  return response;
};

export const fetchPaperAnalysisThread = async ({ userId, searchRunId }) => {
  const token = getAccessToken();
  const response = await fetch(
    `${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(
      userId,
    )}/paper-analysis/search-runs/${encodeURIComponent(searchRunId)}`,
    {
      headers: authJsonHeaders(token),
    },
  );
  if (response.status === 404) {
    return { notFound: true, data: null };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return { notFound: false, data: await response.json() };
};

export const fetchSearchEngagement = async ({ userId, searchId }) => {
  const token = getAccessToken();
  const response = await fetch(
    `${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(
      userId,
    )}/searches/${encodeURIComponent(searchId)}/engagement`,
    {
      headers: authJsonHeaders(token),
    },
  );
  if (response.status === 404) {
    return { notFound: true, data: null };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return { notFound: false, data: await response.json() };
};

export const fetchPaperAnalysisEngagement = async ({ userId, searchRunId }) => {
  const token = getAccessToken();
  const response = await fetch(
    `${getApiUrl()}/api/v1/admin/users/${encodeURIComponent(
      userId,
    )}/paper-analysis/search-runs/${encodeURIComponent(
      searchRunId,
    )}/engagement`,
    {
      headers: authJsonHeaders(token),
    },
  );
  if (response.status === 404) {
    return { notFound: true, data: null };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return { notFound: false, data: await response.json() };
};
